import { HttpContext, HttpHandlerFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { ApiException } from "@api/apiExceptions";
import { VoltaAuthService } from "@core/auth/volta-auth.service";
import { CompanyService } from "@core/client/company.service";
import { ErrorService } from "@core/common/error.service";
import { EntityCannotBeDeleted, FallbackError, InvalidError, InvalidLoginError, InvalidResetPasswordTokenError, RequiresModuleSetting, RequiresTwoFactorError, RevisionNumberMismatchError, UnauthorisedError, UserExceededMaximumSessionsError, VoltaServiceDownError } from "@core/common/errors";
import { VoltaCommon } from "@core/common/volta-common";
import { catchError, switchMap, throwError } from "rxjs";
import { VoltaApiHeader } from "./common.api";
import { NAVIGATE_ON_ERROR, REFRESH_ON_UNAUTHORISE } from "./context-tokens.api";

export function addAccessTokenHeader(request:HttpRequest<unknown>, next:HttpHandlerFn){

    let authService = inject(VoltaAuthService);

    console.log(`Requesting: ${request.url}`)

    //If the call belongs to the Volta API
    if(request.url.includes(VoltaCommon.VOLTA_DOMAIN_API)){

        let reqHeader = request.headers;

        if(!reqHeader.get(VoltaApiHeader.Authorization)){

            //If user is authorised, add the access token to the headers
            if(authService.isAuthorised()){
                reqHeader = reqHeader.set(VoltaApiHeader.Authorization, `Bearer ${authService.getAccessToken()}`);
            }

            const req = request.clone({
                headers: reqHeader
            });
        
            return next(req);

        }

    }

    return next(request);
}

export function addCompanyIdentifierHeader(request:HttpRequest<unknown>, next:HttpHandlerFn){

    let companyService = inject(CompanyService);

    //If the call belongs to the Volta API
    if(request.url.includes(VoltaCommon.VOLTA_DOMAIN_API)){

        let reqHeader = request.headers;

        if(!reqHeader.get(VoltaApiHeader.CompanyIdentifier)){

            const companyId = companyService.company()?.id;

            //If user is in a company, add the company id to the headers
            if(companyId){
                reqHeader = reqHeader.set(VoltaApiHeader.CompanyIdentifier, `${companyId}`);
            }

            const req = request.clone({
                headers: reqHeader
            });
        
            return next(req);
        }

    }

    return next(request);
}

export function handleErrorInterceptor(request:HttpRequest<unknown>, next:HttpHandlerFn){

    const errorService = inject(ErrorService);

    const NAVIGATE = request.context.get(NAVIGATE_ON_ERROR);

    return next(request).pipe(
        catchError((thrownError) => {

            let error:Error = new Error();

            //Defaults
            switch(thrownError?.status){
                case 0:
                    error = new VoltaServiceDownError();
                    break;
                case 400:
                    error = new InvalidError();
                    break;
                case 401:
                    error = new UnauthorisedError();
                    break;
                case 403:
                    error = errorService.onAccessDenied(NAVIGATE);
                    break;
                default:
                    error = new FallbackError();
                    break;
            }

            //Improve Error
            if(thrownError?.error?.code){
                switch(thrownError.error.code){

                    case ApiException.StreamLinesRevisionNumberMismatchException:
                        error = new RevisionNumberMismatchError();
                        break;

                    case ApiException.MultiTenantsMissingConfigurationException:
                        error.name = "Missing Configuration";
                        error.message = "The Configuration of this Module is missing."
                        break;

                    case ApiException.IDAuthUserCreationException:
                        if(thrownError.error.variables){
                            if((thrownError.error.variables.includes("DuplicateUserName"))){
                                error.name = "Username Taken";
                                error.message = "The username you've entered is already in use, please use another one."
                            }
                            else if((thrownError.error.variables.includes("DuplicateEmail"))){
                                error.name = "Email In Use";
                                error.message = "The email you've entered is already in use, please use another one."
                            }
                        }
                        break;

                    case ApiException.MultiTenantsMissingConfigurationException:
                        error.message = "Missing Company Advanced Setting."

                        if(thrownError.error.variables){
                            const firstModule = Object.keys(thrownError.error.variables).at(0);

                            if(firstModule){
                                const missingSetting = thrownError.error.variables[firstModule].at(0);
                                
                                if(missingSetting){
                                    error = new RequiresModuleSetting(missingSetting);
                                }
                            }
                        }
                        break;

                    case ApiException.CurrenciesCurrencyNotFoundException:
                        error.message = "The company's Currency could not be found or is not set."
                        break;

                    case ApiException.GeneralAccountPeriodNotFoundException:
                        error.message = "The current Accounting Period is not open."
                        break;

                    case ApiException.GeneralTransactionCodeNotFoundException:
                        error.message = "The company's Transaction Code could not be found or is incorrect."
                        break;

                    case ApiException.AccessGateSystemRoleDeletionNotAllowedException:
                        error.name = "Deletion Not Allowed";
                        error.message = "System Defined roles cannot be deleted."
                        break;

                    case 'ModelStateValidationFilter':
                        error.name = "Missing Field";
                        error.message = "Some necessary information is missing."
                        break;

                    case ApiException.StreamLinesEntityCannotBeDeletedException:
                        error = new EntityCannotBeDeleted();
                        break;

                    case ApiException.IDAuthUserExceededMaximumNumberOfSessionsException:
                        error = new UserExceededMaximumSessionsError();
                        errorService.onUserExceededMaximumSessions();
                        break;

                    case ApiException.IDAuthUnauthorisedAccessException:
                        error = errorService.onAccessDenied(NAVIGATE);
                        break;

                    case ApiException.ManagementLicenseNotAvailableException:
                        error = errorService.onCompanyLicenseRequired(NAVIGATE);
                        break;

                    case ApiException.ManagementUserInsufficientLicenseException:
                        error = errorService.onInsufficientLicense(NAVIGATE);
                        break;

                    case ApiException.MultiTenantsTenantRequire2FaEnabledException:
                        error = errorService.onCompanyRequiresTFA(NAVIGATE);
                        break;

                    case ApiException.AssetRegisterAssetMissingDivisionException:
                        error.name = "Missing Divison";
                        error.message = "Asset does not have a division assigned."
                        break;

                    case ApiException.AssetRegisterInvalidNominalAccountException:
                        error.name = "Invalid Account";
                        error.message = "A selected account is invalid."
                        break;

                    case ApiException.AssetRegisterSupplierNameNotProvidedException:
                        error.name = "Missing Supplier";
                        error.message = "The Supplier fields are required."
                        break;

                    default:
                        break;

                }
            }
            else if(thrownError?.error?.title == "One or more validation errors occurred."){
                if(thrownError.error.errors){
                    if(thrownError.error.errors.InvalidToken){
                        error = new InvalidResetPasswordTokenError();
                    }
                    else if(thrownError.error.errors.includes('$')){ // TODO: this has to be cleared up, what is '$' ?
                        error.message = "Some necessary information is missing."
                    }
                    else{
                        error.message = thrownError.error.errors[Object.keys(thrownError.error.errors)[0]];
                    }
                }
            }
            else if(thrownError?.error?.detail){
                switch(thrownError.error.detail){
                    case "RequiresTwoFactor":
                        error = new RequiresTwoFactorError();
                        break;
                    case "LockedOut":
                        error.name = "User Locked";
                        error.message = "This user has been locked due to repeated failed tries. Please try again shortly."
                        break;
                    case "Unauthorized":
                        error = new InvalidLoginError();
                        break;
                    case "Failed":
                        error = new InvalidLoginError();
                        break;
                }
            }

            //Fallback
            if(error instanceof FallbackError){
                //Use API's Fallback
                if(thrownError?.error?.message){
                    error.message = thrownError?.error?.message 
                }
            }

            return throwError(() => error);
        })
    )

}

export function refreshOnUnauthoriseInterceptor(request:HttpRequest<unknown>, next:HttpHandlerFn){

    const errorService = inject(ErrorService);
    const authService = inject(VoltaAuthService);

    const REFRESH = request.context.get(REFRESH_ON_UNAUTHORISE);

    return next(request).pipe(
        catchError((error) => {

            //Attempt refresh call on 401 or Auth issues.
            //TODO: Works as intended but does not queue the calls and if more than 1 call is done at the same time and they will all return a 401, the refresh call is made for each failure.
            //TODO: Some sort of queue that when a refresh call is made all of the call made will then be put in a queue or will wait for the singular refresh to end and then retry.
            //TODO: the check also needs to happen in an interceptor BEFORE the call (if a refresh is happenining, wait anyway)
            if((error.status && error.status == 401)
            || (error?.error?.code && <ApiException>error.error.code == ApiException.IDAuthSessionNotFoundException)){
                if(REFRESH){
                    return authService.refreshToken(false)
                    .pipe(
                        switchMap(() => {

                            //Update with new Access Token
                            let reqHeader = request.headers;

                            if(authService.isAuthorised()){
                                reqHeader = reqHeader.set(VoltaApiHeader.Authorization, `Bearer ${authService.getAccessToken()}`);
                            }

                            const req = request.clone({
                                headers: reqHeader,
                                context: new HttpContext().set(REFRESH_ON_UNAUTHORISE, false)
                            });

                            return next(req)
                        })
                    )
                }
                else{
                    errorService.onUserUnauthorised();
                }
            }

            return throwError(() => error);
        })
    )

}